import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { font, Panel, StatCard, Avatar, Badge, Button, EmptyState } from "@realtyflows/ui";
import { useTheme } from "../contexts/ThemeContext";
import {
  getBrokerCompliance,
  getBrokerPerformance,
  getBrokerAtRisk,
  getBrokerColdClients,
  getBrokerForecast,
  getBrokerBriefing,
  generateBrokerBriefing,
} from "../api";
import useResponsive from "../hooks/useResponsive";

/**
 * BrokerDashboard — V3 Features 3.1, 3.8-3.11
 *
 * The broker-only command center. Requires the user to be team owner.
 * Rolls up:
 *   - Compliance gaps across the team
 *   - Agent production metrics
 *   - At-risk agents
 *   - Cold client recovery report
 *   - Pipeline revenue forecast
 *
 * Tabbed layout; default tab is the compliance overview (most actionable).
 *
 * Props:
 *   user — current user; must own the active team
 */
export default function BrokerDashboard({ user }) {
  const t = useTheme();  // V2 ADR-003 Phase 4.3 wave 8
  const navigate = useNavigate();
  const responsive = useResponsive();
  const teamId = user?.team_id;

  const [tab, setTab] = useState("compliance");
  const [compliance, setCompliance] = useState(null);
  const [performance, setPerformance] = useState([]);
  const [atRisk, setAtRisk] = useState([]);
  const [coldClients, setColdClients] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  // V3 Feature 3.7: regenerate the AI briefing on demand
  const handleRegenerateBriefing = async () => {
    setBriefingLoading(true);
    try {
      const fresh = await generateBrokerBriefing(teamId);
      setBriefing(fresh);
    } catch (err) {
      window.alert(err.message || "Couldn't generate briefing");
    } finally {
      setBriefingLoading(false);
    }
  };

  useEffect(() => {
    if (!teamId) { setLoading(false); return; }
    Promise.all([
      getBrokerCompliance(teamId),
      getBrokerPerformance(teamId, 90),
      getBrokerAtRisk(teamId),
      getBrokerColdClients(teamId, 30),
      getBrokerForecast(teamId),
      getBrokerBriefing(teamId),
    ])
      .then(([c, p, ar, cc, f, b]) => {
        setCompliance(c); setPerformance(p || []); setAtRisk(ar || []);
        setColdClients(cc || []); setForecast(f); setBriefing(b);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [teamId]);

  if (!teamId) {
    return (
      <Panel padding={28} style={{ maxWidth: 600, margin: "0 auto", textAlign: "center" }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: t.textPrimary, marginBottom: 8, fontFamily: font.display }}>
          Broker Dashboard
        </div>
        <div style={{ fontSize: 13, color: t.textMuted, lineHeight: 1.6, fontFamily: font.body }}>
          The broker dashboard is for team owners only. Create or join a team to access it.
        </div>
      </Panel>
    );
  }

  if (loading) {
    return <div style={{ padding: 40, textAlign: "center", color: t.textMuted, fontFamily: font.body }}>Loading broker dashboard...</div>;
  }

  const fmtMoney = (n) => {
    if (n == null || n === 0) return "$0";
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
    return `$${Math.round(n)}`;
  };

  const tabs = [
    { id: "compliance", label: "Compliance" },
    { id: "performance", label: "Agent Performance" },
    { id: "risk", label: `At-Risk Agents${atRisk.length ? ` (${atRisk.length})` : ""}` },
    { id: "cold", label: `Cold Clients${coldClients.length ? ` (${coldClients.length})` : ""}` },
    { id: "forecast", label: "Revenue Forecast" },
  ];

  return (
    <div style={{ animation: "fadeSlide 0.3s ease" }}>
      {/* Hero stats */}
      <div style={{ display: "grid", gridTemplateColumns: responsive.isCompact ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 14, marginBottom: 20 }}>
        <StatCard label="Open Folders" value={String(compliance?.totals?.openFolders || 0)} delay="0s" />
        <StatCard label="Pending Review" value={String(compliance?.totals?.pendingReview || 0)} delay="0.07s" />
        <StatCard label="With Missing Docs" value={String(compliance?.totals?.foldersWithMissing || 0)} delay="0.14s" />
        <StatCard label="Forecasted GCI" value={fmtMoney(forecast?.forecastedOpenGci)} delay="0.21s" />
      </div>

      {/* V3 Feature 3.7: AI briefing card — at top so it's the first thing the broker reads */}
      <Panel padding={20} style={{ marginBottom: 16, background: `linear-gradient(135deg, ${t.navyLight || t.surface}, ${t.surface})` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>
              ✨ Weekly Brief
            </div>
            {briefing && (
              <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2, fontFamily: font.body }}>
                Generated {new Date(briefing.created_at).toLocaleString()}
              </div>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={handleRegenerateBriefing} disabled={briefingLoading}>
            {briefingLoading ? "Thinking..." : briefing ? "Regenerate" : "Generate brief"}
          </Button>
        </div>
        {briefing ? (
          <div style={{ fontSize: 13, color: t.textPrimary, fontFamily: font.body, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
            {briefing.content}
          </div>
        ) : (
          <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body, fontStyle: "italic" }}>
            Generate a personalized AI brief covering your team's top performers, at-risk agents, urgent folders, and one concrete action for today.
          </div>
        )}
      </Panel>

      {/* Tab bar */}
      <div style={{ display: "flex", gap: 0, marginBottom: 16, background: t.pageBg, borderRadius: 10, padding: 3, border: `1px solid ${t.border}`, flexWrap: "wrap" }}>
        {tabs.map((tb) => (
          <button
            key={tb.id}
            onClick={() => setTab(tb.id)}
            style={{
              flex: 1, padding: "10px 16px", textAlign: "center", borderRadius: 8,
              fontSize: 12, fontWeight: 600, fontFamily: font.body,
              cursor: "pointer", transition: "all 0.2s", border: "none",
              background: tab === tb.id ? t.surface : "transparent",
              color: tab === tb.id ? t.textPrimary : t.textMuted,
              boxShadow: tab === tb.id ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
              minWidth: 120,
            }}
          >
            {tb.label}
          </button>
        ))}
      </div>

      {/* Compliance tab */}
      {tab === "compliance" && compliance && (
        <div style={{ display: "grid", gridTemplateColumns: responsive.isCompact ? "1fr" : "2fr 1fr", gap: 16 }}>
          <Panel padding={20}>
            <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, marginBottom: 12, fontFamily: font.display }}>
              Folders Needing Attention
            </div>
            {compliance.attentionFolders.length === 0 ? (
              <EmptyState title="All clear" sub="No folders need attention right now." />
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {compliance.attentionFolders.map((f) => (
                  <div
                    key={f.id}
                    onClick={() => navigate(`/contracts/folders/${f.id}`)}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: 10, background: t.surfaceWarm,
                      border: `1px solid ${f.status === "pending_review" ? t.amber + "40" : t.border}`,
                      borderRadius: 8, cursor: "pointer", transition: "border-color 0.15s",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = t.emerald; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = f.status === "pending_review" ? t.amber + "40" : t.border; }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, fontFamily: font.body, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {f.name}
                      </div>
                      <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2, fontFamily: font.body }}>
                        {f.agentName} {f.clientName ? `· ${f.clientName}` : ""}
                      </div>
                    </div>
                    {f.missingRequired > 0 && <Badge color={t.coral}>{f.missingRequired} missing</Badge>}
                    {f.pendingApproval > 0 && <Badge color={t.sky}>{f.pendingApproval} to approve</Badge>}
                    {f.status === "pending_review" && <Badge color={t.amber}>Review</Badge>}
                  </div>
                ))}
              </div>
            )}
            <div style={{ marginTop: 14, textAlign: "center" }}>
              <Button variant="primary" onClick={() => navigate(`/broker/audit`)}>
                Start Quick Audit →
              </Button>
            </div>
          </Panel>

          <Panel padding={20}>
            <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, marginBottom: 12, fontFamily: font.display }}>
              Per-Agent Compliance
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {compliance.byAgent.filter((a) => a.folderCount > 0).slice(0, 12).map((a) => (
                <div key={a.agentId} style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: 8, background: t.surfaceWarm,
                  border: `1px solid ${t.border}`, borderRadius: 8,
                }}>
                  <Avatar src={a.agentPhoto} name={a.agentName} size={28} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, fontFamily: font.body, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {a.agentName}
                    </div>
                    <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono }}>
                      {a.folderCount} folders
                    </div>
                  </div>
                  {a.foldersWithMissing > 0 && <Badge color={t.coral}>{a.foldersWithMissing} gaps</Badge>}
                  {a.stalled > 0 && <Badge color={t.amber}>{a.stalled} stalled</Badge>}
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* Performance tab */}
      {tab === "performance" && (
        <Panel padding={20}>
          <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>
            Agent Performance — Last 90 Days
          </div>
          {performance.length === 0 ? (
            <EmptyState title="No agents yet" sub="Invite agents to start seeing team performance." />
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: font.body }}>
                <thead>
                  <tr style={{ background: t.surfaceWarm, color: t.textMuted, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    {["Agent", "Closed", "GCI", "Pipeline", "Avg Days to Close", "Avg Response", "Folders", "Client Score"].map((h) => (
                      <th key={h} style={{ padding: "8px 10px", textAlign: "left", borderBottom: `1px solid ${t.border}`, fontFamily: font.mono, fontWeight: 700 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {performance.map((p) => (
                    <tr key={p.agentId} style={{ borderBottom: `1px solid ${t.border}` }}>
                      <td style={{ padding: 10 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <Avatar src={p.agentPhoto} name={p.agentName} size={24} />
                          <span style={{ fontWeight: 600, color: t.textPrimary }}>{p.agentName}</span>
                        </div>
                      </td>
                      <td style={{ padding: 10, fontFamily: font.mono, fontWeight: 700 }}>{p.dealsClosed}</td>
                      <td style={{ padding: 10, fontFamily: font.mono }}>{fmtMoney(p.gciEstimate)}</td>
                      <td style={{ padding: 10, fontFamily: font.mono }}>{p.dealsInPipeline}</td>
                      <td style={{ padding: 10, fontFamily: font.mono }}>{p.avgDaysToClose ? `${Math.round(p.avgDaysToClose)}d` : "—"}</td>
                      <td style={{ padding: 10, fontFamily: font.mono }}>{p.avgResponseHours ? `${p.avgResponseHours.toFixed(1)}h` : "—"}</td>
                      <td style={{ padding: 10, fontFamily: font.mono }}>{p.foldersManaged}</td>
                      <td style={{ padding: 10, fontFamily: font.mono }}>{p.avgClientScore ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}

      {/* At-risk tab */}
      {tab === "risk" && (
        <Panel padding={20}>
          <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>
            At-Risk Agents
          </div>
          {atRisk.length === 0 ? (
            <EmptyState title="Everyone looks healthy" sub="No agents are showing risk signals right now." />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {atRisk.map((a) => (
                <div key={a.agentId} style={{
                  padding: 14, borderRadius: 10,
                  background: a.severity === "high" ? t.coral + "10" : t.amber + "10",
                  border: `1px solid ${a.severity === "high" ? t.coral + "40" : t.amber + "40"}`,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <Avatar src={a.agentPhoto} name={a.agentName} size={32} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, fontFamily: font.body }}>{a.agentName}</div>
                      <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono }}>
                        {a.snapshot.dealsClosed} closed · {a.snapshot.dealsInPipeline} in pipeline · {a.snapshot.foldersManaged} folders
                      </div>
                    </div>
                    <Badge color={a.severity === "high" ? t.coral : t.amber}>{a.severity.toUpperCase()}</Badge>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingLeft: 42 }}>
                    {a.reasons.map((r, i) => (
                      <div key={i} style={{ fontSize: 12, color: t.textSecondary, fontFamily: font.body }}>• {r.message}</div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}

      {/* Cold clients tab */}
      {tab === "cold" && (
        <Panel padding={20}>
          <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>
            Cold Client Recovery — Team-Wide
          </div>
          {coldClients.length === 0 ? (
            <EmptyState title="No cold clients" sub="Every client has been touched recently." />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {coldClients.map((c) => (
                <div key={c.clientId}
                  onClick={() => navigate(`/clients/detail?clientId=${c.clientId}`)}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: 10, background: t.surfaceWarm,
                    border: `1px solid ${t.border}`, borderRadius: 8, cursor: "pointer",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, fontFamily: font.body, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {c.name}
                    </div>
                    <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>
                      Owned by {c.agentName} · last contact {c.daysSilent}d ago
                    </div>
                  </div>
                  {c.score != null && <Badge color={c.score >= 70 ? t.emerald : c.score >= 40 ? t.amber : t.coral}>Score {c.score}</Badge>}
                  <Badge color={t.coral}>{c.daysSilent}d silent</Badge>
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}

      {/* Forecast tab */}
      {tab === "forecast" && forecast && (
        <div style={{ display: "grid", gridTemplateColumns: responsive.isCompact ? "1fr" : "1fr 1fr", gap: 16 }}>
          <Panel padding={20}>
            <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>
              Revenue Forecast
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <ForecastRow label="Forecasted (weighted by stage)" value={fmtMoney(forecast.forecastedOpenGci)} color={t.emerald} big />
              <ForecastRow label="Pipeline total (unweighted)" value={fmtMoney(forecast.pipelineTotalGci)} />
              <ForecastRow label="Closed YTD" value={fmtMoney(forecast.closedGciYtd)} color={t.sky} />
              <ForecastRow label="Open deals" value={String(forecast.openDealsCount)} />
            </div>
          </Panel>

          <Panel padding={20}>
            <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>
              Forecasted GCI by Agent
            </div>
            {forecast.byAgent.length === 0 ? (
              <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body }}>No open deals.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {forecast.byAgent.map((a) => (
                  <div key={a.agentId} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${t.border}`, fontFamily: font.body, fontSize: 12 }}>
                    <span>{a.agentName} <span style={{ color: t.textMuted, fontFamily: font.mono }}>({a.deals} deals)</span></span>
                    <span style={{ fontFamily: font.mono, fontWeight: 700, color: t.textPrimary }}>{fmtMoney(a.weightedGci)}</span>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
      )}
    </div>
  );
}

function ForecastRow({ label, value, color = "currentColor", big }) {
  const t = useTheme();
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: big ? "10px 14px" : "6px 0", background: big ? t.surfaceWarm : "transparent", borderRadius: big ? 8 : 0 }}>
      <span style={{ fontSize: big ? 12 : 11, color: t.textMuted, fontFamily: font.body }}>{label}</span>
      <span style={{ fontSize: big ? 22 : 14, fontWeight: 800, color, fontFamily: font.mono }}>{value}</span>
    </div>
  );
}
